import type { ReviewData, CriterionData } from './evaluation';

// マージステップの定義
export type MergeStep = 1 | 2 | 3 | 4 | 5 | 6;

// マージアクション
export type MergeAction = 'keep_first' | 'keep_second' | 'keep_both' | 'merge';

export const MERGE_STEPS = [
  { step: 1, title: 'プロジェクト選択', description: '統合対象のプロジェクトを選択' },
  { step: 2, title: '評価対象統合', description: '評価対象の重複検出と統合' },
  { step: 3, title: '観点統合', description: '評価観点の統合と調整' },
  { step: 4, title: 'ルーブリック統合', description: 'ルーブリックの調整' },
  { step: 5, title: '評価統合', description: '評価位置の統合' },
  { step: 6, title: '等級統合', description: '等級システムの統合' }
] as const;

// メインのワークスペース
export interface MergeWorkspace {
  id: string;
  sourceProjects: [string, string];
  sourceProjectTitles: [string, string];
  currentStep: MergeStep;
  stepData: {
    targets?: MergedTargets;
    aspects?: MergedAspects;
    rubric?: MergedRubric;
    evaluations?: MergedEvaluations;
    grades?: MergedGrades;
  };
  finalProjectTitle: string;
  createdAt: Date;
  updatedAt: Date;
}

// 評価対象統合データ
export interface MergedTargets {
  originalProjects: Record<string, ReviewData[]>;
  duplicates: DuplicateTarget[];
  merged: ReviewData[];
  userEdited: boolean;
}

export interface DuplicateTarget {
  indices: [number, number]; // [project1Index, project2Index]
  similarity: number;
  action: 'keep_first' | 'keep_second' | 'keep_both' | 'merge'; // ユーザー選択
  mergedData?: ReviewData; // マージを選択した場合の統合データ
}

// 観点統合データ
export interface MergedAspects {
  originalProjects: Record<string, CriterionData[]>;
  duplicates: DuplicateAspect[];
  merged: CriterionData[];
  userEdited: boolean;
}

export interface DuplicateAspect {
  indices: [number, number];
  similarity: number;
  action: 'merge' | 'keep_first' | 'keep_second' | 'keep_both';
}

// ルーブリック統合データ
export interface MergedRubric {
  rubricData: any[]; // 詳細は後で定義
  analysis: Map<string, any>;
  userEdited: boolean;
}

// 評価統合データ
export interface MergedEvaluations {
  originalProjects: Record<string, any[]>;
  merged: any[];
  adjustments: EvaluationAdjustment[];
  userEdited: boolean;
}

export interface EvaluationAdjustment {
  targetId: string;
  aspectId: string;
  originalPosition: number;
  newPosition: number;
  source: 'project1' | 'project2' | 'manual';
}

// 等級統合データ
export interface MergedGrades {
  originalProjects: Record<string, any[]>;
  merged: any[];
  boundaries: GradeBoundary[];
  userEdited: boolean;
}

export interface GradeBoundary {
  grade: string;
  minScore: number;
  maxScore: number;
  color?: string;
}

// ユーティリティ型
export interface ProjectInfo {
  id: string;
  title: string;
  productCount: number;
  criteriaCount: number;
  status: 'InProgress' | 'Completed';
  createdAt: string;
  updatedAt: string;
}

// マージワークスペースの操作結果
export interface MergeResult {
  success: boolean;
  workspace?: MergeWorkspace;
  error?: string;
}

// 類似度計算結果
export interface SimilarityResult {
  index1: number;
  index2: number;
  similarity: number;
  item1: any;
  item2: any;
}

// マージ設定
export interface MergeOptions {
  similarityThreshold: number; // デフォルト: 0.8
  autoSave: boolean; // デフォルト: true
  preserveOriginals: boolean; // デフォルト: true
}

// セッション管理
export interface MergeSession {
  workspaces: MergeWorkspace[];
  currentWorkspaceId?: string;
  lastAccessed: Date;
}